import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Play, Zap, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  queued: { color: '#8A8A9A', icon: Clock, label: 'Queued', desc: 'Waiting in GPU queue' },
  processing: { color: '#FF6B4A', icon: Play, label: 'Processing', desc: 'Rendering on GPU cluster' },
  completed: { color: '#FF6B4A', icon: CheckCircle2, label: 'Completed', desc: 'Render complete' },
  failed: { color: '#EF4444', icon: AlertCircle, label: 'Failed', desc: 'Error encountered' },
};

const SERVICE_LABELS = { ai_image: 'AI Image', '3d_render': '3D Render', data_viz: 'Data Processing', video_render: 'Video' };

export default function JobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);

  const fetchJob = useCallback(async () => {
    try { const res = await axios.get(`${API}/jobs/${jobId}`, { withCredentials: true }); setJob(res.data); }
    catch (err) { /* polling errors are non-critical */ }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => {
    fetchJob();
    const interval = setInterval(fetchJob, 3000);
    return () => clearInterval(interval);
  }, [fetchJob]);

  const simulateProgress = async () => {
    setSimulating(true);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/simulate`, {}, { withCredentials: true });
      setJob(res.data);
      toast.success(`${res.data.status} — ${res.data.progress}%`);
    } catch { toast.error('Simulation failed'); }
    finally { setSimulating(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 text-[#8A8A9A] animate-spin" /></div>;
  if (!job) return (
    <div className="text-center py-16">
      <p className="text-[#8A8A9A] text-sm">Job not found.</p>
      <Link to="/dashboard/jobs"><Button variant="ghost" className="mt-3 text-xs text-[#8A8A9A]">Back to Queue</Button></Link>
    </div>
  );

  const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-2xl space-y-8" data-testid="job-detail-page">
      <Link to="/dashboard/jobs" className="flex items-center gap-1.5 text-xs text-[#8A8A9A] hover:text-[#1A1A2E] transition-colors" data-testid="back-to-jobs">
        <ArrowLeft className="w-3.5 h-3.5" /> Queue
      </Link>

      <div className="p-8 rounded-lg" style={{ background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl font-light tracking-tight text-[#1A1A2E] mb-3" data-testid="job-title">{job.title}</h1>
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className="text-[10px] rounded-md px-2 py-0.5 bg-transparent border" style={{ color: sc.color, borderColor: `${sc.color}30` }}>
                <StatusIcon className="w-3 h-3 mr-1" /> {sc.label}
              </Badge>
              <span className="font-mono text-[10px] text-[#8A8A9A] uppercase tracking-wider">{SERVICE_LABELS[job.service_type]}</span>
            </div>
          </div>
          {job.status !== 'completed' && job.status !== 'failed' && (
            <Button
              onClick={simulateProgress}
              disabled={simulating}
              className="bg-[#FF6B4A] text-white font-medium rounded-md hover:bg-[#FF5533] text-xs h-8 px-4"
              data-testid="simulate-btn"
            >
              {simulating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3 h-3 mr-1" /> Simulate</>}
            </Button>
          )}
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-[#8A8A9A] font-mono uppercase tracking-wider">Progress</span>
            <span className="font-mono text-sm" style={{ color: sc.color }}>{job.progress}%</span>
          </div>
          <div className="h-1.5 w-full bg-[#FFF0EA] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${job.progress}%`, background: sc.color }} />
          </div>
          <p className="text-[10px] text-[#8A8A9A] mt-2">{sc.desc}</p>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-6 border-t border-gray-100">
          <div>
            <div className="text-[10px] text-[#8A8A9A] font-mono uppercase tracking-wider mb-1">Cost</div>
            <div className="flex items-center gap-1 font-mono text-sm text-[#FF6B4A]"><Zap className="w-3.5 h-3.5" /> {job.cost} cr</div>
          </div>
          <div>
            <div className="text-[10px] text-[#8A8A9A] font-mono uppercase tracking-wider mb-1">Created</div>
            <div className="text-xs text-[#1A1A2E]">{new Date(job.created_at).toLocaleDateString()}</div>
          </div>
          <div>
            <div className="text-[10px] text-[#8A8A9A] font-mono uppercase tracking-wider mb-1">Updated</div>
            <div className="text-xs text-[#1A1A2E]">{new Date(job.updated_at).toLocaleDateString()}</div>
          </div>
          <div>
            <div className="text-[10px] text-[#8A8A9A] font-mono uppercase tracking-wider mb-1">Service</div>
            <div className="text-xs text-[#1A1A2E]">{SERVICE_LABELS[job.service_type]}</div>
          </div>
        </div>
      </div>

      {job.description && (
        <div className="p-6 rounded-lg" style={{ background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.04)' }}>
          <h3 className="text-[10px] text-[#8A8A9A] font-mono uppercase tracking-wider mb-3">Description</h3>
          <p className="text-sm text-[#4A4A5A] leading-relaxed">{job.description}</p>
        </div>
      )}

      {job.config && Object.keys(job.config).length > 0 && (
        <div className="p-6 rounded-lg" style={{ background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.04)' }}>
          <h3 className="text-[10px] text-[#8A8A9A] font-mono uppercase tracking-wider mb-3">Configuration</h3>
          <pre className="font-mono text-xs text-[#8A8A9A] bg-black/20 p-4 rounded-md overflow-x-auto">{JSON.stringify(job.config, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
